import React from "react";
import ToggleField from "../../../components/inputs/toggleField";
import RichTextEditor from "../../../views/pages/components/richTextEditor";
import InputField from "../../../components/inputs/inputField";
import FilesUploaderModal from "../../../components/files/filesUploaderModal";
import FileChooserPreview from "../../../components/inputs/fileChooserPreview";
import BadgePreview from "./badgePreview";
import SelectField from "../../../components/inputs/selectField";
import API from "../../../utils/API";

class CheckinSetup extends React.Component {
    constructor(props) {
        super(props);
        const boldsignIntegrationEnabled = props.organization?.integrations?.find(integration => integration.identifier === 'boldsign')?.enabled || false;
        this.state = {
            boldsignIntegrationEnabled,
            // Third-party scanner vendors the API says this org can use, as { label, value } options
            scannerOptions: [],
            defaultScannerProvider: ''
        };
    }

    componentDidMount() {
        this.mounted = true;
        API().get(`Organizations/${this.props.orgId}/thirdPartyScannerProviders`).then((res) => {
            if (this.mounted && res.data) {
                this.setState({
                    scannerOptions: res.data.providers.map(provider => ({ label: provider.name, value: provider.identifier })),
                    defaultScannerProvider: res.data.defaultProvider
                });
            }
        }).catch((error) => {
            console.error('Failed to load third-party scanner providers:', error);
        });
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    render() {
        const { checkInAppConfiguration, event, orgId, eventId, handleChange, uploadingCheckinBanner, updateUploadingCheckinBanner } = this.props;
        const { scannerOptions, defaultScannerProvider } = this.state;

        // Events saved before the provider setting existed use the API's default
        const selectedProvider = checkInAppConfiguration.thirdPartyScannerProvider || defaultScannerProvider;

        return (
            <div className="sectionBody">
                <div className='blocksContainer'>
                    <div className='flex w-100'>
                        <div className="sectionBlock mr-25 contained-h">
                            <h2 className='blockHeader'>Admin</h2>
                            <InputField
                                label="Input 4 digit pin"
                                required={true}
                                value={checkInAppConfiguration.pin}
                                type='number'
                                maxLength={4}
                                onChange={(e) => {
                                    // Only allow 4 digit pin
                                    const pinValue = e.target.value.toString().slice(0, 4);
                                    handleChange({ pin: pinValue });
                                }}
                            />
                            <h2 className='blockHeader'>Setup</h2>
                            <ToggleField
                                label="Allow Attendee Walk Ins"
                                checked={checkInAppConfiguration.enableWalkInCheckIn}
                                disabled={!event.allowPublicRegistrations}
                                alert={event.allowPublicRegistrations ? null : 'Public registrations are disabled'}
                                icons={false}
                                onChange={(e) => {
                                    handleChange({ enableWalkInCheckIn: e.target.checked, useCheckinFormForWalkIn: true });
                                }}
                            />
                            <ToggleField
                                label="Enable Session Check in"
                                checked={checkInAppConfiguration.enableSessionCheckIn}
                                icons={false}
                                onChange={(e) => {
                                    handleChange({ enableSessionCheckIn: e.target.checked, useCheckinFormForSession: true });
                                }}
                            />
                            {checkInAppConfiguration.enableSessionCheckIn &&
                                <InputField
                                    label="Input 4 digit pin for session check in"
                                    required={true}
                                    value={checkInAppConfiguration.sessionPin}
                                    type='number'
                                    maxLength={4}
                                    onChange={(e) => {
                                        // Only allow 4 digit pin
                                        const pinValue = e.target.value.toString().slice(0, 4);
                                        handleChange({ sessionPin: pinValue });
                                    }}
                                />
                            }

                            <ToggleField
                                label="Enable QR Scanner for Check In"
                                checked={checkInAppConfiguration.enableQRScanner}
                                icons={false}
                                onChange={(e) => {
                                    handleChange({ enableQRScanner: e.target.checked });
                                }}
                            />
                            {checkInAppConfiguration.enableQRScanner && scannerOptions.length > 0 && (
                            <ToggleField
                                label="Enable Third Party Scanner Integration"
                                checked={checkInAppConfiguration.enableThirdPartyScannerIntegration}
                                icons={false}
                                onChange={(e) => {
                                    const changes = { enableThirdPartyScannerIntegration: e.target.checked };
                                    // Save a provider that is actually enabled, so the app doesn't fall back to one the org doesn't have
                                    if (e.target.checked && !scannerOptions.some(option => option.value === selectedProvider)) {
                                        changes.thirdPartyScannerProvider = scannerOptions[0].value;
                                    }
                                    handleChange(changes);
                                }}
                            />)}
                            {checkInAppConfiguration.enableQRScanner && checkInAppConfiguration.enableThirdPartyScannerIntegration && scannerOptions.length > 1 && (
                            <SelectField
                                label="Third Party Scanner"
                                options={scannerOptions}
                                value={scannerOptions.find(option => option.value === selectedProvider)}
                                onChange={(option) => {
                                    handleChange({ thirdPartyScannerProvider: option.value });
                                }}
                            />)}
                            <ToggleField
                                label="Enable Badge Printing"
                                checked={checkInAppConfiguration.enableBadgePrinting}
                                icons={false}
                                onChange={(e) => {
                                    handleChange({ enableBadgePrinting: e.target.checked });
                                }}
                            />

                            <div className="editorFileInput">
                                <FileChooserPreview
                                    label="Background Image"
                                    value={checkInAppConfiguration.backgroundBanner}
                                    clearFile={() => {
                                        handleChange({ backgroundBanner: "" });
                                    }}
                                    onClick={(e) => {
                                        updateUploadingCheckinBanner(true);
                                    }}
                                />
                                <FilesUploaderModal
                                    title="Image"
                                    links={{
                                        getAssets: !this.props.programId ? `Organizations/${this.props.orgId}/events/${this.props.eventId}/assets` : `Organizations/${this.props.orgId}/assets`,
                                        addAssets: !this.props.programId ? `Organizations/${this.props.orgId}/events/${this.props.eventId}/assets` : `Organizations/${this.props.orgId}/assets`,
                                    }}
                                    isOpen={uploadingCheckinBanner}
                                    toggle={() => {
                                        updateUploadingCheckinBanner(false);
                                    }}
                                    orgId={orgId}
                                    addFiles={(fileURL) => {
                                        handleChange({ backgroundBanner: fileURL });
                                        updateUploadingCheckinBanner(false);
                                    }}
                                />
                            </div>

                            <h2 className='blockHeader'>Flow</h2>
                            <ToggleField
                                label="Show Event App QR Code"
                                checked={checkInAppConfiguration.showQRCode}
                                onChange={(e) => {
                                    handleChange({ showQRCode: e.target.checked });
                                }}
                                icons={false}
                            />
                            <ToggleField
                                label="Enable Consent Screen"
                                checked={checkInAppConfiguration.enableConsentScreen}
                                onChange={(e) => {
                                    console.log(e.target.checked);
                                    handleChange({ enableConsentScreen: e.target.checked });
                                }}
                                icons={false}
                            />
                            {checkInAppConfiguration.enableConsentScreen && <RichTextEditor
                                label="Consent Content"
                                required={true}
                                value={checkInAppConfiguration.consentScreen}
                                onChange={(value) => {
                                    handleChange({ consentScreen: value });
                                }}
                            />}
                            {this.state.boldsignIntegrationEnabled && <ToggleField
                                label="Enable Document Signing"
                                checked={checkInAppConfiguration.documentSigningConfig?.enabled}
                                onChange={(e) => {
                                    handleChange({ 
                                        documentSigningConfig: {
                                            ...checkInAppConfiguration.documentSigningConfig,
                                            enabled: e.target.checked
                                        }
                                    });
                                }}
                                icons={false}
                            />}
                        </div>

                        {/* Badge Preview - only show when badge printing is enabled */}
                        {!this.props.programId && checkInAppConfiguration.enableBadgePrinting && (
                            <div className="sectionBlock">
                                <BadgePreview
                                    badgeConfig={checkInAppConfiguration.badgeConfiguration}
                                    onEdit={() => {
                                        // Open the badge editor modal
                                        this.props.onOpenBadgeEditor && this.props.onOpenBadgeEditor();
                                    }}
                                    organizationFonts={this.props.organization?.branding?.fonts || []}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default CheckinSetup;